"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.storeLinesExporter = exports.productTotalsExporter = void 0;
/** What Excel reads as the start of a formula rather than as text. */
const FORMULA_LEAD = /^[=+\-@]/;
/**
 * RFC 4180: a field is quoted only when it has to be (comma, quote or line
 * break inside), doubling any inner quote. Numeric-only files come out exactly
 * as before the names were added.
 *
 * A TEXT field opening with `=`, `+`, `-` or `@` also gets a leading
 * apostrophe. This file is opened in Excel by design — that is why its header
 * is in Spanish — and Excel EVALUATES such a cell: a dish renamed from the
 * backoffice would become a formula in whoever's spreadsheet. Quoting alone
 * does not stop it. The guard is on strings only, so a negative quantity stays
 * a number, and it costs a visible apostrophe on a name no dish or store
 * legitimately has. The `rows` handed to the screen carry the name untouched.
 */
const csvField = (value) => {
    const text = typeof value === 'number' || !FORMULA_LEAD.test(value) ? String(value) : `'${value}`;
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};
const toCsv = (header, rows) => [header, ...rows].map((fields) => fields.map(csvField).join(',')).join('\n');
/**
 * Sums the quantities of every line, grouped by what `groupOf` returns (the
 * `key` identifies the group; `group` is what the row will carry). Zeros do not
 * survive: an opted-out order (all lines zero) — or a product that nets to zero
 * across the week — produces no row, since there is nothing to produce.
 */
const sumLines = (orders, groupOf) => {
    const totals = new Map();
    for (const order of orders) {
        for (const line of order.products) {
            const { key, group } = groupOf(order, line.productId);
            const acc = totals.get(key);
            if (acc)
                acc.quantity += line.quantity;
            else
                totals.set(key, { group, quantity: line.quantity });
        }
    }
    return [...totals.values()].filter((total) => total.quantity !== 0);
};
const PRODUCT_TOTALS_HEADER = ['Catálogo', 'Id producto', 'Part No.', 'Plato', 'Cantidad'];
/**
 * The production view: quantities per (catalog, product) for the week, across
 * every store. The week itself is not a column — the caller already narrowed
 * the orders to one, and the file is named after it.
 */
exports.productTotalsExporter = {
    format: 'csv',
    export: (orders, lookups) => {
        const totals = sumLines(orders, (order, productId) => ({
            key: `${order.catalogId}|${productId}`,
            group: { catalogId: order.catalogId, productId },
        }));
        const rows = totals.map(({ group: { catalogId, productId }, quantity }) => {
            const product = lookups.products.get(productId);
            return { catalogId, productId, partNo: product?.externalId ?? '', productName: product?.name ?? '', quantity };
        });
        rows.sort((rowA, rowB) => rowA.catalogId - rowB.catalogId || rowA.productId - rowB.productId);
        const csv = toCsv(PRODUCT_TOTALS_HEADER, rows.map((row) => [row.catalogId, row.productId, row.partNo, row.productName, row.quantity]));
        return { csv, rows };
    },
};
const STORE_LINES_HEADER = ['Id tienda', 'Tienda', 'Código MRP', 'Id producto', 'Part No.', 'Plato', 'Cantidad'];
/**
 * The per-store breakdown: quantities per (store, product) for the week. A
 * store with two closed orders in the week (proposal + extra) sums into one
 * row per product. A store or product the lookups do not know keeps its id and
 * leaves the text cells empty — a closed order still has to be exported.
 */
exports.storeLinesExporter = {
    format: 'csv',
    export: (orders, lookups) => {
        const totals = sumLines(orders, (order, productId) => ({
            key: `${order.storeId}|${productId}`,
            group: { storeId: order.storeId, productId },
        }));
        const rows = totals.map(({ group: { storeId, productId }, quantity }) => {
            const store = lookups.stores.get(storeId);
            const product = lookups.products.get(productId);
            return {
                storeId,
                storeName: store?.name ?? '',
                customerCode: store?.customerCode ?? '',
                productId,
                partNo: product?.externalId ?? '',
                productName: product?.name ?? '',
                quantity,
            };
        });
        rows.sort((rowA, rowB) => rowA.storeId - rowB.storeId || rowA.productId - rowB.productId);
        const csv = toCsv(STORE_LINES_HEADER, rows.map((row) => [row.storeId, row.storeName, row.customerCode, row.productId, row.partNo, row.productName, row.quantity]));
        return { csv, rows };
    },
};
//# sourceMappingURL=mrp-export.js.map